/**
 * Presentation-agnostic view-model for the WorkBar agents row.
 *
 * Consumes a {@link ThreadTeam} (from `selectThreadTeamByRef`) and produces
 * either `null` (hide) or the pre-sorted roster plus header metadata needed
 * to render the row. The React shell below this file stays a thin projection
 * so it can be snapshot-tested with purely synthetic inputs.
 */

import type { ThreadId, ThreadStatus } from "@cmmd-forge/contracts";

import type { ThreadTeam } from "~/storeSelectors";
import type { ThreadShell } from "~/types";
import {
  buildTeamPanelViewModel,
  pickAgentsHeaderLabel,
  type AgentsHeaderLabel,
  type TeamPanelViewModel,
  type TeamPanelWorkerInput,
} from "./workbarAgents.logic";

export interface WorkBarAgentsRowViewModel {
  readonly header: AgentsHeaderLabel;
  readonly team: TeamPanelViewModel;
  readonly isMaster: boolean;
  readonly isWorker: boolean;
  /** Active thread id — used to highlight the "you" row on worker views. */
  readonly activeThreadId: ThreadId;
}

/**
 * Build the WorkBar agents row view-model.
 *
 * Hide (return `null`) when:
 *   1. The active thread has no team context (shouldn't happen — selector
 *      returns the caller itself), OR
 *   2. The active thread is a master with **zero** worker siblings. Worker
 *      threads always render so the breadcrumb to master stays visible.
 */
export function buildAgentsRowViewModel(
  team: ThreadTeam | undefined,
): WorkBarAgentsRowViewModel | null {
  if (!team) return null;
  const { you, master, workers } = team;
  const isMaster = you.kind === "master";
  const isWorker = you.kind === "worker";

  if (isMaster && workers.length === 0) return null;

  const workerInputs: TeamPanelWorkerInput[] = workers.map((w) => shellToWorkerInput(w));

  const currentWorkerId = isWorker ? you.id : undefined;
  const teamVm = buildTeamPanelViewModel(
    workerInputs,
    currentWorkerId !== undefined ? { currentWorkerId } : {},
  );

  const masterLink =
    isWorker && master !== undefined ? { threadId: master.id, title: master.title } : undefined;

  const header = pickAgentsHeaderLabel({
    isMaster,
    isWorker,
    workerCount: workers.length,
    ...(masterLink !== undefined ? { masterLink } : {}),
  });

  return {
    header,
    team: teamVm,
    isMaster,
    isWorker,
    activeThreadId: you.id,
  };
}

/**
 * Project a `ThreadShell` down to the minimal record the roster renderer
 * needs. Kept as a named export so tests and the `browser.tsx` fixture can
 * fabricate inputs without constructing a full ThreadShell.
 */
export function shellToWorkerInput(shell: ThreadShell): TeamPanelWorkerInput {
  const updatedAtMs = shell.updatedAt ? Date.parse(shell.updatedAt) : undefined;
  return {
    id: shell.id,
    title: shell.title || "Worker",
    branch: shell.branch ?? "",
    status: shell.status as ThreadStatus,
    workerRole: shell.workerRole,
    ...(updatedAtMs !== undefined && !Number.isNaN(updatedAtMs) ? { updatedAt: updatedAtMs } : {}),
  };
}
